<template>
  <section v-if="labExperienceCollection" class="labs-collection">
    <ActionLoader
      v-if="isFetching"
      :size="0.5"
      color="'#ffffff"
      name="line-spin-fade-loader"
      class="labs-collection__details-spinner"
    />
    <div
      v-else
      :class="[
        'labs-collection__content',
        `labs-collection__content--${labsCollectionInfo.background}`,
      ]"
    >
      <h1 class="labs-collection__title">
        {{ labsCollectionInfo.title }}
        <PillBadge
          v-if="labsCollectionInfo.isBeta"
          translucid
          class="labs-collection__is-beta"
        >
          BETA
        </PillBadge>
      </h1>
      <div class="labs-collection__details">
        <div class="labs-collection__details-pill-wrapper">
          <div class="labs-collection__details-pill" />
          <div class="labs-collection__details-pill-border" />
          <PillBadge translucid>
            <strong>{{ `${labsCollectionInfo.labs} ` }}</strong>
            hands-on Labs
          </PillBadge>
        </div>
        <Difficulty
          v-if="!isFetching"
          class="labs-collection__details-difficulty"
          :difficulty="labsCollectionInfo.difficulty"
          :alwaysVisible="true"
        />
        <div class="labs-collection__details-path-separator" />
        <div class="labs-collection__details-path">
          <component
            :is="labsCollectionInfo.lpIcon"
            class="labs-collection__details-path-icon"
          />
          <div class="labs-collection__details-path-name">
            {{ labsCollectionInfo.lpTitle }}
          </div>
        </div>
      </div>
      <div class="labs-collection__description">
        {{ labsCollectionInfo.description }}
      </div>
      <div class="labs-collection__labs-collection">
        <ActionLoader
          v-if="isFetchingCollection"
          color="'#ffffff"
          name="line-spin-fade-loader"
        />
        <ContentList
          v-else
          :items="labsCollection"
          :isFreeTrialUser="isFreeTrialUser"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import CyberSecurity from '@/assets/images/learning-areas/noborders/cybersecurity.svg'
import ContentList from '@/components/search/ContentList.vue'
import PillBadge from '@/components/shared/PillBadge.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Difficulty from '@/components/properties/Difficulty.vue'

export default {
  name: 'LabsCollection',
  components: {
    CyberSecurityIcon: { ...CyberSecurity, name: 'CyberSecurityIcon' },
    PillBadge,
    ActionLoader,
    Difficulty,
    ContentList,
  },
  data() {
    return {
      isFetching: true,
      isFetchingCollection: true,
      labsCollectionInfo: {},
      labsCollectionIds: [],
    }
  },
  computed: {
    ...mapState({
      currentCourse: state => state.courses.currentCourse,
      labs: state => state.content.currentLabs,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
      labExperienceCollection: 'app/labExperienceCollection',
    }),
    labsCollection() {
      return this.labs.map(lab => ({
        ...lab,
        ...{
          content_type: 'lab',
          objectID: lab.id,
          parent_id: this.currentCourse.id,
          parent_type: 'course',
        },
      }))
    },
  },
  created() {
    if (this.labExperienceCollection) {
      try {
        this.isFetching = true
        this.setLabsCollectionInfo()
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
    } else {
      this.$router.push({
        name: 'skill-dive',
      })
    }
  },
  methods: {
    async setLabsCollectionInfo() {
      try {
        await this.$store.dispatch(
          'courses/getCourseById',
          this.$route.params.id
        )
        if (this.currentCourse.slug === 'secure-coding-beta') {
          this.labsCollectionInfo = {
            ...this.currentCourse,
            background: this.currentCourse.slug,
            title: this.currentCourse.name.replace(' (Beta)', ''),
            isBeta: this.currentCourse.slug.indexOf('beta') > -1,
            labs: this.currentCourse.summary.total_labs,
            lpIcon: `${this.currentCourse.learning_areas[0]?.name.replace(
              / +/g,
              ''
            )}Icon`,
            lpTitle: this.currentCourse.learning_areas[0]?.name,
            description: `Designed for aspiring ethical hackers, security professionals, and enthusiasts,
              this lab collection offers hands-on labs that challenge your skills in the art of
              exploitation and post-exploitation techniques.`,
          }
        } else {
          this.labsCollectionInfo = {
            ...this.currentCourse,
          }
        }
        if (this.currentCourse.content[0]?.content) {
          this.labsCollectionIds = this.currentCourse.content[0]?.content[0]?.content.map(
            lab => lab.uuid
          )
        }
        this.isFetching = false
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
      try {
        if (this.labsCollectionIds) {
          await this.$store.dispatch('content/getLabs', {
            labIds: this.labsCollectionIds,
          })
        }
        this.isFetchingCollection = false
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
    },
  },
}
</script>

<style lang="scss">
.labs-collection {
  &__content {
    color: var(--white);
    padding: 90px 20px;
    text-align: center;
    &--secure-coding-beta {
      background: rgb(50, 0, 110);
      background: linear-gradient(
        180deg,
        rgba(50, 0, 110, 1) 20%,
        rgba(22, 20, 30, 1) 50%
      );
    }
    @include mobile {
      padding-top: 70px;
    }
    @include desktop {
      padding-top: 115pxpx;
    }
  }
  &__title {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: rem(32px);
    line-height: rem(38px);
    font-weight: 450;
    @include mobile {
      font-size: rem(32px);
      line-height: rem(38px);
      font-weight: 450;
    }
    @include desktop {
      font-size: rem(32px);
      line-height: rem(38px);
      font-weight: 450;
    }
  }
  &__is-beta {
    margin-left: 15px;
    padding: 9px 14px !important;
  }
  &__details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
    row-gap: 10px;
    padding-bottom: 20px;
    @include mobile {
      width: 500px;
      margin: auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &-pill {
      &-wrapper {
        justify-self: center;
        grid-column: 1/3;
        position: relative;
        top: -13px;
        .pill {
          text-transform: uppercase;
        }
      }
      opacity: 0.3;
      &-border {
        position: relative;
        top: 25px;
        border: grey solid 1px;
        opacity: 1;
        height: 26px;
        border-radius: 20px;
      }
    }
    &-difficulty {
      justify-content: right;
      .difficulty__label {
        text-transform: uppercase;
      }
      @include mobile {
        width: unset !important;
      }
    }
    &-path {
      align-items: center;
      justify-content: left;
      grid-area: 2/2/2/2;
      display: flex;
      text-transform: uppercase;
      @include mobile {
        margin-left: -25px;
      }
      &-separator {
        width: 1px;
        height: 30px;
        background: var(--glass-solid-mid-gray);
        grid-area: 2/2/2/2;
        position: relative;
        right: 10px;
      }
    }
  }
  &__description {
    font-size: rem(14px);
    font-weight: 450;
    line-height: rem(20px);
    padding-bottom: 40px;
    @include mobile {
      width: 70%;
      margin: auto;
    }
    @include desktop {
      max-width: 1200px;
      margin: auto;
    }
  }
  &__labs-collection {
    max-width: 1200px;
    margin: auto;
    .content-row {
      background: var(--cod-gray);
    }
    .content-row__main-container__info__top {
      text-align: left;
    }
    .content-row__details-container {
      max-height: 1100px;
      text-align: left;
    }
    .content-row__main-container__info__title {
      text-align: left;
    }
    .content-row__main-container__info__top {
      flex-flow: row;
      column-gap: 15px;
    }
    .content-row__main-container__info__top__content-type--lab-running {
      display: block;
    }
  }
}
</style>
